import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Calendar, Award, CheckCircle, Clock, AlertCircle, RotateCcw } from "lucide-react";
import apiClient from "../../../utils/apiClient";
import { AppBadge, EmptyState } from "../../ui";
import "./general-assignments.scss";

const statusConfig = {
  pending: { label: "Pending", color: "amber", icon: Clock },
  submitted: { label: "Submitted", color: "blue", icon: CheckCircle },
  graded: { label: "Graded", color: "emerald", icon: Award },
  resubmit_requested: { label: "Resubmit Required", color: "rose", icon: RotateCcw },
  overdue: { label: "Overdue", color: "rose", icon: AlertCircle },
};

const getStudentStatus = (assignment) => {
  if (assignment.submission) return assignment.submission.status;
  if (new Date(assignment.dueDate) < new Date() && assignment.status !== "closed") return "overdue";
  return "pending";
};

const StudentGeneralAssignments = ({ batchId }) => {
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const params = batchId ? `?batchId=${batchId}` : '';
    apiClient.get(`/general-assignments/student/list${params}`)
      .then(({ data }) => setAssignments(data.assignments || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [batchId]);

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

  if (loading) return <div className="ga-loading">Loading assignments...</div>;

  if (!assignments.length) {
    return (
      <EmptyState
        icon={<Award size={40} />}
        title="No assignments yet"
        description="Your batch has no general assignments right now"
      />
    );
  }

  return (
    <div className="ga-student-list">
      {assignments.map((a) => {
        const status = getStudentStatus(a);
        const cfg = statusConfig[status] || statusConfig.pending;
        const StatusIcon = cfg.icon;

        return (
          <div
            key={a._id}
            className="ga-student-list__card"
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/s/batch-assignment/${a._id}`)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') navigate(`/s/batch-assignment/${a._id}`); }}
          >
            <div className="ga-student-list__card-body">
              <div className="ga-student-list__card-title">{a.title}</div>
              <div className="ga-student-list__card-meta">
                <span className="ga-student-list__card-meta-item">
                  <Calendar size={14} />
                  Due: {formatDate(a.dueDate)}
                </span>
                <span className="ga-student-list__card-meta-item">
                  <Award size={14} />
                  {a.maxMarks} marks
                </span>
                {a.submission?.marks != null && (
                  <span className="ga-student-list__card-meta-item">
                    <CheckCircle size={14} />
                    Score: {a.submission.marks}/{a.maxMarks}
                  </span>
                )}
              </div>
            </div>
            <div className="ga-student-list__card-right">
              <AppBadge variant="outline" color={cfg.color}>
                <StatusIcon size={12} /> {cfg.label}
              </AppBadge>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default StudentGeneralAssignments;
